import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { 
  FileText, 
  Download, 
  Calendar,
  DollarSign,
  Receipt,
  FileCheck,
  TrendingUp,
  Filter,
  Printer
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";
import FinancialsLock from "@/components/FinancialsLock";

const BASE_API = process.env.REACT_APP_BACKEND_URL;

function TaxReportsBody() {
  const { theme } = useTheme();
  const { businessSlug } = useParams();
  const isDark = theme === "dark";
  
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [dateRange, setDateRange] = useState("this_year");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  
  const API = `${BASE_API}/api/b/${businessSlug}`;

  useEffect(() => {
    fetchTaxReport();
  }, [businessSlug, dateRange]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/info`);
        if (!cancelled) setBusinessInfo(res.data);
      } catch (err) {
        // non-blocking
      }
    })();
    return () => { cancelled = true; };
  }, [businessSlug]);

  const fetchTaxReport = async () => {
    setLoading(true);
    try {
      const params = { range: dateRange };
      if (dateRange === "custom" && customStart && customEnd) {
        params.start_date = customStart;
        params.end_date = customEnd;
      }
      
      const response = await axios.get(`${API}/reports/tax`, { params });
      setReportData(response.data);
    } catch (error) {
      console.error("Failed to fetch tax report:", error);
      toast.error("Failed to load tax report");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Backend stores tax_rate as a decimal (e.g. 0.075 = 7.5%, 0.08 = 8%).
  // Render it as a real percentage so reports don't read "0.080%".
  const formatTaxRate = (rate) => {
    const r = Number(rate) || 0;
    // Heuristic: if value already > 1, treat as already-percent (e.g. 7.5 -> 7.50%)
    const pct = r > 1 ? r : r * 100;
    return `${pct.toFixed(2)}%`;
  };

  const handleExportCSV = () => {
    if (!reportData) return;
    
    // Create CSV content
    let csv = "Tax Report - " + reportData.period_label + "\n\n";
    csv += "SUMMARY\n";
    csv += "Total Revenue," + reportData.summary.total_revenue + "\n";
    csv += "Taxable Sales," + reportData.summary.taxable_sales + "\n";
    csv += "Tax Exempt Sales," + reportData.summary.tax_exempt_sales + "\n";
    csv += "Total Tax Collected," + reportData.summary.total_tax_collected + "\n";
    csv += "Average Tax Rate," + reportData.summary.average_tax_rate + "%\n\n";
    
    csv += "TAXED INVOICES\n";
    csv += "Invoice #,Date,Customer,Subtotal,Tax Rate,Tax Amount,Total\n";
    reportData.taxed_invoices?.forEach(inv => {
      csv += `${inv.invoice_number},${inv.date},${inv.customer_name},${inv.subtotal},${formatTaxRate(inv.tax_rate)},${inv.tax_amount},${inv.total}\n`;
    });
    
    csv += "\nTAX EXEMPT INVOICES\n";
    csv += "Invoice #,Date,Customer,Reason,Total\n";
    reportData.exempt_invoices?.forEach(inv => {
      csv += `${inv.invoice_number},${inv.date},${inv.customer_name},${inv.exempt_reason || 'Not specified'},${inv.total}\n`;
    });
    
    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tax_report_${dateRange}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success("Tax report exported!");
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in print:space-y-3 print:bg-white print:text-black print:text-[10px]" data-testid="tax-reports-page">
      {/* Print-only global rules: hide app chrome (sidebar, mobile nav, back btn), tighten tables */}
      <style>{`
        @media print {
          @page { size: Letter; margin: 0.45in 0.45in 0.5in 0.45in; }
          html, body { background: #fff !important; color: #000 !important; }
          aside, nav, .lg\\:hidden, [data-testid="back-btn-desktop"], [data-testid="back-btn-mobile"] { display: none !important; }
          main { padding: 0 !important; margin: 0 !important; }
          /* Tighten everything for print */
          .print-table { font-size: 9.5pt !important; }
          .print-table th, .print-table td { padding: 4px 6px !important; line-height: 1.25 !important; }
          .print-table thead { background: #f3f4f6 !important; }
          .print-card { box-shadow: none !important; border: 1px solid #d1d5db !important; }
          .print-summary-cell { padding: 8px 12px !important; }
          h1.print-title { font-size: 16pt !important; }
          .print-letterhead-name { font-size: 14pt !important; }
          tr, td, th { page-break-inside: avoid !important; }
          thead { display: table-header-group !important; }
          .badge { padding: 1px 5px !important; font-size: 8pt !important; }
        }
      `}</style>

      {/* Header (screen only) */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 print:hidden">
        <div>
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
            Tax Reports
          </h1>
          <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            Track your sales tax for easy tax season preparation
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className={`w-40 ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white'}`}>
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="last_month">Last Month</SelectItem>
              <SelectItem value="this_quarter">This Quarter</SelectItem>
              <SelectItem value="last_quarter">Last Quarter</SelectItem>
              <SelectItem value="this_year">This Year</SelectItem>
              <SelectItem value="last_year">Last Year</SelectItem>
              <SelectItem value="all_time">All Time</SelectItem>
              <SelectItem disabled className="text-xs text-slate-500 font-semibold">── Months {new Date().getFullYear()} ──</SelectItem>
              <SelectItem value="jan_current">January {new Date().getFullYear()}</SelectItem>
              <SelectItem value="feb_current">February {new Date().getFullYear()}</SelectItem>
              <SelectItem value="mar_current">March {new Date().getFullYear()}</SelectItem>
              <SelectItem value="apr_current">April {new Date().getFullYear()}</SelectItem>
              <SelectItem value="may_current">May {new Date().getFullYear()}</SelectItem>
              <SelectItem value="jun_current">June {new Date().getFullYear()}</SelectItem>
              <SelectItem value="jul_current">July {new Date().getFullYear()}</SelectItem>
              <SelectItem value="aug_current">August {new Date().getFullYear()}</SelectItem>
              <SelectItem value="sep_current">September {new Date().getFullYear()}</SelectItem>
              <SelectItem value="oct_current">October {new Date().getFullYear()}</SelectItem>
              <SelectItem value="nov_current">November {new Date().getFullYear()}</SelectItem>
              <SelectItem value="dec_current">December {new Date().getFullYear()}</SelectItem>
              <SelectItem disabled className="text-xs text-slate-500 font-semibold">── Months {new Date().getFullYear() - 1} ──</SelectItem>
              <SelectItem value="jan_last">January {new Date().getFullYear() - 1}</SelectItem>
              <SelectItem value="feb_last">February {new Date().getFullYear() - 1}</SelectItem>
              <SelectItem value="mar_last">March {new Date().getFullYear() - 1}</SelectItem>
              <SelectItem value="apr_last">April {new Date().getFullYear() - 1}</SelectItem>
              <SelectItem value="may_last">May {new Date().getFullYear() - 1}</SelectItem>
              <SelectItem value="jun_last">June {new Date().getFullYear() - 1}</SelectItem>
              <SelectItem value="jul_last">July {new Date().getFullYear() - 1}</SelectItem>
              <SelectItem value="aug_last">August {new Date().getFullYear() - 1}</SelectItem>
              <SelectItem value="sep_last">September {new Date().getFullYear() - 1}</SelectItem>
              <SelectItem value="oct_last">October {new Date().getFullYear() - 1}</SelectItem>
              <SelectItem value="nov_last">November {new Date().getFullYear() - 1}</SelectItem>
              <SelectItem value="dec_last">December {new Date().getFullYear() - 1}</SelectItem>
            </SelectContent>
          </Select>
          
          <Button 
            onClick={handleExportCSV}
            variant="outline"
            className={isDark ? 'border-slate-700 hover:bg-slate-800' : ''}
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          
          <Button 
            onClick={handlePrint}
            variant="outline"
            className={isDark ? 'border-slate-700 hover:bg-slate-800' : ''}
          >
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      {/* PRINT-ONLY professional letterhead + summary strip */}
      <div className="hidden print:block">
        <div className="flex items-start justify-between border-b-2 border-black pb-3 mb-4">
          <div>
            <p className="print-letterhead-name font-bold uppercase tracking-tight leading-tight">
              {businessInfo?.name || (businessSlug || '').toUpperCase()}
            </p>
            <p className="text-[9pt] text-gray-700 leading-snug">
              {businessInfo?.address || ''}
            </p>
            <p className="text-[9pt] text-gray-700 leading-snug">
              {[businessInfo?.phone, businessInfo?.email].filter(Boolean).join(' · ')}
            </p>
          </div>
          <div className="text-right">
            <h1 className="print-title font-bold tracking-tight uppercase">Sales Tax Report</h1>
            <p className="text-[9pt] text-gray-700">Period: <span className="font-semibold text-black">{reportData?.period_label}</span></p>
            <p className="text-[8pt] text-gray-500">Generated {new Date().toLocaleDateString('en-US', { year:'numeric', month:'long', day:'numeric' })}</p>
          </div>
        </div>

        {/* Summary strip — 4 columns, no big icons, all on one line at the top */}
        <table className="w-full border border-gray-400 mb-4" style={{ borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              {[
                { label: 'Total Revenue', value: formatCurrency(reportData?.summary?.total_revenue) },
                { label: 'Taxable Sales', value: formatCurrency(reportData?.summary?.taxable_sales), sub: `${reportData?.summary?.taxed_count || 0} invoices` },
                { label: 'Tax Collected', value: formatCurrency(reportData?.summary?.total_tax_collected), sub: `Avg rate: ${reportData?.summary?.average_tax_rate || 0}%`, accent: true },
                { label: 'Tax Exempt Sales', value: formatCurrency(reportData?.summary?.tax_exempt_sales), sub: `${reportData?.summary?.exempt_count || 0} invoices` },
              ].map((s, i) => (
                <td key={i} className="print-summary-cell border-r border-gray-400 last:border-r-0 align-top" style={{ width: '25%' }}>
                  <p className="text-[8pt] uppercase tracking-wider text-gray-600 mb-0.5">{s.label}</p>
                  <p className={`text-[12pt] font-bold leading-tight ${s.accent ? 'text-black underline decoration-black' : 'text-black'}`}>{s.value}</p>
                  {s.sub && <p className="text-[8pt] text-gray-600 mt-0.5">{s.sub}</p>}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Summary Cards (screen only — print uses the clean letterhead strip above) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 print:hidden">
        {/* Total Revenue */}
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Total Revenue
                </p>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  {formatCurrency(reportData?.summary?.total_revenue)}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        {/* Taxable Sales */}
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Taxable Sales
                </p>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  {formatCurrency(reportData?.summary?.taxable_sales)}
                </p>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  {reportData?.summary?.taxed_count || 0} invoices
                </p>
              </div>
              <Receipt className="w-8 h-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        {/* Tax Collected */}
        <Card className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'} ring-2 ring-green-500/50`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Tax Collected
                </p>
                <p className="text-2xl font-bold text-green-500">
                  {formatCurrency(reportData?.summary?.total_tax_collected)}
                </p>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Avg rate: {reportData?.summary?.average_tax_rate || 0}%
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        {/* Tax Exempt */}
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Tax Exempt Sales
                </p>
                <p className={`text-2xl font-bold text-purple-500`}>
                  {formatCurrency(reportData?.summary?.tax_exempt_sales)}
                </p>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  {reportData?.summary?.exempt_count || 0} invoices
                </p>
              </div>
              <FileCheck className="w-8 h-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Taxed Invoices Table */}
      {/* Taxed Invoices Table */}
      <Card className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'} print-card`}>
        <CardHeader className="print:py-2">
          <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'} print:text-black print:text-[11pt]`}>
            <Receipt className="w-5 h-5 text-green-500 print:hidden" />
            Taxed Invoices ({reportData?.taxed_invoices?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="print:p-0">
          {reportData?.taxed_invoices?.length > 0 ? (
            <div className="overflow-x-auto">
              <Table className="print-table">
                <TableHeader>
                  <TableRow className={isDark ? 'border-slate-700' : ''}>
                    <TableHead className={isDark ? 'text-slate-300' : ''}>Invoice #</TableHead>
                    <TableHead className={isDark ? 'text-slate-300' : ''}>Date</TableHead>
                    <TableHead className={isDark ? 'text-slate-300' : ''}>Customer</TableHead>
                    <TableHead className={`text-right ${isDark ? 'text-slate-300' : ''}`}>Subtotal</TableHead>
                    <TableHead className={`text-right ${isDark ? 'text-slate-300' : ''}`}>Tax Rate</TableHead>
                    <TableHead className={`text-right ${isDark ? 'text-slate-300' : ''}`}>Tax Amount</TableHead>
                    <TableHead className={`text-right ${isDark ? 'text-slate-300' : ''}`}>Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportData.taxed_invoices.map((invoice) => (
                    <TableRow key={invoice.id} className={isDark ? 'border-slate-700 hover:bg-slate-700/50' : 'hover:bg-slate-50'}>
                      <TableCell className={`font-medium ${isDark ? 'text-white' : ''}`}>
                        {invoice.invoice_number}
                      </TableCell>
                      <TableCell className={isDark ? 'text-slate-300' : ''}>
                        {formatDate(invoice.date)}
                      </TableCell>
                      <TableCell className={isDark ? 'text-slate-300' : ''}>
                        {invoice.customer_name}
                      </TableCell>
                      <TableCell className={`text-right ${isDark ? 'text-slate-300' : ''}`}>
                        {formatCurrency(invoice.subtotal)}
                      </TableCell>
                      <TableCell className={`text-right ${isDark ? 'text-slate-300' : ''}`}>
                        {formatTaxRate(invoice.tax_rate)}
                      </TableCell>
                      <TableCell className="text-right text-green-500 font-medium">
                        {formatCurrency(invoice.tax_amount)}
                      </TableCell>
                      <TableCell className={`text-right font-bold ${isDark ? 'text-white' : ''}`}>
                        {formatCurrency(invoice.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              No taxed invoices in this period
            </p>
          )}
        </CardContent>
      </Card>

      {/* Tax Exempt Invoices Table */}
      {/* Tax Exempt Invoices Table */}
      <Card className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'} print-card`}>
        <CardHeader className="print:py-2">
          <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'} print:text-black print:text-[11pt]`}>
            <FileCheck className="w-5 h-5 text-purple-500 print:hidden" />
            Tax Exempt Invoices ({reportData?.exempt_invoices?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="print:p-0">
          {reportData?.exempt_invoices?.length > 0 ? (
            <div className="overflow-x-auto">
              <Table className="print-table">
                <TableHeader>
                  <TableRow className={isDark ? 'border-slate-700' : ''}>
                    <TableHead className={isDark ? 'text-slate-300' : ''}>Invoice #</TableHead>
                    <TableHead className={isDark ? 'text-slate-300' : ''}>Date</TableHead>
                    <TableHead className={isDark ? 'text-slate-300' : ''}>Customer</TableHead>
                    <TableHead className={isDark ? 'text-slate-300' : ''}>Exempt Reason</TableHead>
                    <TableHead className={`text-right ${isDark ? 'text-slate-300' : ''}`}>Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportData.exempt_invoices.map((invoice) => (
                    <TableRow key={invoice.id} className={isDark ? 'border-slate-700 hover:bg-slate-700/50' : 'hover:bg-slate-50'}>
                      <TableCell className={`font-medium ${isDark ? 'text-white' : ''}`}>
                        {invoice.invoice_number}
                      </TableCell>
                      <TableCell className={isDark ? 'text-slate-300' : ''}>
                        {formatDate(invoice.date)}
                      </TableCell>
                      <TableCell className={isDark ? 'text-slate-300' : ''}>
                        {invoice.customer_name}
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                          {invoice.exempt_reason || 'Not specified'}
                        </Badge>
                      </TableCell>
                      <TableCell className={`text-right font-bold ${isDark ? 'text-white' : ''}`}>
                        {formatCurrency(invoice.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              No tax exempt invoices in this period
            </p>
          )}
        </CardContent>
      </Card>

      {/* Tax Tips */}
      <Card className={`${isDark ? 'bg-amber-900/20 border-amber-700' : 'bg-amber-50 border-amber-200'} print:hidden`}>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <FileText className="w-5 h-5 text-amber-500 mt-0.5" />
            <div>
              <h3 className={`font-semibold ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                Tax Season Tips
              </h3>
              <ul className={`text-sm mt-2 space-y-1 ${isDark ? 'text-amber-200/70' : 'text-amber-700'}`}>
                <li>• Keep this report for your records - export as CSV for your accountant</li>
                <li>• Tax exempt customers should have exemption certificates on file</li>
                <li>• Sales tax is typically due quarterly - check your state requirements</li>
                <li>• The "Tax Collected" amount is what you owe to the state</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Public wrapper — Tax Reports contains revenue & tax-owed numbers that
 * the owner doesn't want their cashier or counter staff to see, so we
 * gate it behind the same Financials privacy PIN. If no PIN is set the
 * lock no-ops and lets the page render straight through.
 */
export default function TaxReports() {
  const { businessSlug } = useParams();
  return (
    <FinancialsLock businessSlug={businessSlug}>
      <TaxReportsBody />
    </FinancialsLock>
  );
}
